// The Spotify catalogue import — read the band's released history, bring the chosen records in.
//
// Read is a plain band-keyed query; the import invalidates the releases hub so the new shells appear without a
// reload. Both are `releases:write` server-side (importing creates releases), so the modal is only reachable
// from a seat that could have typed them by hand.
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";
import { releasesPrefix } from "./useReleases.js";
import { useQueryClient } from "@tanstack/react-query";
import { useBandContext } from "../bandContext.js";

export type SpotifyCatalogAlbum = components["schemas"]["SpotifyCatalogAlbum"];
export type SpotifyCatalogListing = components["schemas"]["SpotifyCatalogListing"];
export type SpotifyCatalogImportResult = components["schemas"]["SpotifyCatalogImportResult"];
export type SpotifyCatalogLink = components["schemas"]["SpotifyCatalogLink"];

/** What the review step sends: albums to create, and albums to attach to an existing release instead. */
export interface SpotifyCatalogImportInput {
  albumIds: string[];
  links: SpotifyCatalogLink[];
}

/**
 * `GET /bands/{bandId}/releases/catalog/spotify` — the band's released catalogue as Spotify has it, each album
 * flagged with whether it is already in the hub. READ-ONLY, so opening the picker creates nothing.
 *
 * This reaches a vendor, so it must not fire on every Releases page view. The gate is the CONDITIONAL MOUNT —
 * the modal is only rendered once someone opens the importer — not an `enabled` flag; the hook had one that no
 * caller ever passed, which reads as caller-controlled safety that isn't actually wired to anything.
 */
export function useSpotifyCatalog() {
  const { api } = useApp();
  return useBandQuery<SpotifyCatalogListing>(
    ["spotify-catalog"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/releases/catalog/spotify", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    // A back catalogue changes when someone puts out a record. Cache generously; the picker is not a live view.
    { staleTime: 5 * 60_000 },
  );
}

/** `POST …/releases/catalog/spotify/import` — idempotent on the album id, so a double-press cannot fork a
 *  band's catalogue. `links` attach an album to a release the band already made here rather than creating a
 *  second one (ART-274). Invalidates the hub so imported and linked releases appear immediately. */
export function useImportSpotifyCatalog() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();

  return useBandMutation<SpotifyCatalogImportResult, SpotifyCatalogImportInput>(
    async (bandId, { albumIds, links }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/bands/{bandId}/releases/catalog/spotify/import",
        { params: { path: { bandId } }, body: { albumIds, links } },
      );
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: () => {
        if (bandId) void qc.invalidateQueries({ queryKey: releasesPrefix(bandId) });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't import from Spotify")) },
  );
}
